import React from 'react';
import { Typography, Grid } from '@material-ui/core';
import BackButton from 'mobile/components/BackButton';
import { ChevronRight } from '@material-ui/icons';
import withApi from 'common/hoc/withApi';
import * as Sentry from '@sentry/browser';
import history from 'common/constants/config/history';
import Feature from 'common/components/Feature';
import { makeStyles } from '@material-ui/core/styles';
import { MYCASTANDCREW_URL } from 'mobile/constants';

const projectsStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    backgroundColor: '#f7fcfc',
    paddingTop: '20px',
    paddingBottom: '100px',
    minHeight: '100vh',
  },
  container: {
    marginTop: theme.spacing(3),
    overflow: 'scroll',
  },
  contentArea: {
    backgroundColor: '#ffffff',
    boxShadow: '0 -3 7 rgba(0, 0, 0, 0.05)',
    padding: '10px 20px',
    marginTop: '15px',
  },
  moreIcon: {
    color: '#2eaba4',
    height: '24px',
    width: '24px',
  },
  pageTitle: {
    marginLeft: '20px',
    fontFamily: 18,
    color: '#646464',
    fontWeight: 500,
  },
  headerRow: {
    backgroundColor: '#f7fcfc',
    color: '#2eaba4',
    fontSize: '14px',
    fontWeight: 500,
    padding: '0 20px !important',
  },
  projectRow: {
    borderBottom: '1px solid #d8d8d8',
  },
  projectName: {
    fontWeight: 'bold',
    color: '#646464',
    fontSize: 14,
    padding: '15px 24px 15px 0px',
  },
  companyName: {
    color: '#646464',
    fontSize: 14,
    padding: '15px 24px 15px 0px',
  },
  nextIcon: {
    padding: '30px 24px 15px 0px',
  },
  viewMore: {
    color: '#0091ff',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'center',
    marginTop: '30px',
  },
}));

const ProjectList = props => {
  const classes = projectsStyles();

  const { projects: projectsQuery = {}, me: meQuery = {} } = props;
  const { status: projectsStatus, data: { items: projects = [] } = {} } =
    projectsQuery || {};

  const renderProjectRow = (project = {}) => {
    const { privileges } = project || {};
    let to;
    if (privileges) {
      // This user is a studio admin so we figure out where they should go
      // based of the privileges in the projects query and the Privileges to map array
      to = privileges.includes('can_approve_offers')
        ? `/projects/${project.id}/offers`
        : '/projects';
    } else {
      // If a user arrived here then there is a problem with this users permission configurations
      // Or they have been revoked access
      to = '/projects';
      Sentry.captureMessage(
        'Can not find appropriate route for user from project list',
        {
          extra: {
            meQuery,
            projectsQuery,
          },
        },
      );
    }
    return (
      <Grid
        key={project.id}
        data-test-id={`Projects-row-${project.id}`}
        container
        className={classes.projectRow}
        onClick={() => history.push({ pathname: to })}
      >
        <Grid
          data-test-id={`Projects-name-${project.id}`}
          className={classes.projectName}
          item
          xs={4}
        >
          {project.name}
        </Grid>
        <Grid
          className={classes.companyName}
          data-test-id={`Projects-productionCompany-${project.id}`}
          item
          xs={4}
        >
          {project.production_company_name}
        </Grid>
        <Grid
          item
          xs={3}
          className={classes.companyName}
          data-test-id={`Projects-producer-${project.id}`}
        >
          {project.producer_name}
        </Grid>
        <Grid item xs={1} className={classes.nextIcon}>
          <ChevronRight className={classes.moreIcon} />
        </Grid>
      </Grid>
    );
  };

  const isLoading = projectsStatus === 'loading';
  const hasProjects = projects.length > 0;

  return (
    <div className={classes.root}>
      <BackButton
        title="Back to MyCast&Crew"
        url={MYCASTANDCREW_URL}
        isExternal
      />
      <Typography
        data-test-id="Projects-title"
        variant="h4"
        className={classes.pageTitle}
      >
        Projects{' '}
      </Typography>
      {!isLoading && hasProjects && (
        <div className={classes.container}>
          <Grid container className={classes.headerRow}>
            <Grid item xs={4}>
              Project
            </Grid>
            <Grid item xs={4}>
              Production Co.
            </Grid>
            <Grid item xs={3}>
              Producer
            </Grid>
            <Grid item xs={1}></Grid>
          </Grid>

          <div className={classes.contentArea}>
            {projects.map(renderProjectRow)}
          </div>
          <Feature name="viewMoreProjects">
            <Typography className={classes.viewMore}>
              View 10 More Projects
            </Typography>
          </Feature>
        </div>
      )}
      {!isLoading && !hasProjects && (
        <div className={classes.contentArea}>
          <Typography variant="h4">Project not found</Typography>
        </div>
      )}
    </div>
  );
};

ProjectList.queries = {
  projects: {
    info: (params, related) => {
      let query = related['/router/query'];

      return {
        id: '/projects',
        cursor: {
          page: query.page,
          per_page: query.per_page,
          q: query.q,
        },
      };
    },
  },
  me: {
    info: () => {
      return { id: '/me' };
    },
  },
};

export default withApi(ProjectList);
